import type { WidgetFaceProps } from "@/components/shapes/shared/widget-face";
import {
  WIDGET_DISPLAY,
  WIDGET_MUTED,
  WidgetEmpty,
  WidgetFaceRoot,
  WidgetMore,
  WidgetRule,
} from "@/components/shapes/shared/widget-face-parts";
import { fitRows } from "@/components/shapes/shared/widget-rows";
import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { todoFrameMessages } from "@/i18n/messages/frames-todo";
import { cn } from "@/lib/cn";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { useProjectStore } from "@/stores/project-store";
import { todoItemsFrom, type TodoItem } from "./todo-items";
import { TodoEditRow, WidgetCheckMark } from "./todo-edit-row";
import { useTodoEdits } from "./use-todo-edits";

const ROWS = { medium: 4, large: 7 } as const;

/**
 * The rows a size shows (`fitRows`), moved down so the row with `keep` (one
 * just added, or being typed in) is the last one shown.
 */
function rowsShowing(
  items: readonly TodoItem[],
  max: number,
  keep: string | null,
): { shown: readonly TodoItem[]; more: number } {
  const fitted = fitRows(items, max);
  const at = keep ? items.findIndex((item) => item.id === keep) : -1;
  if (at < fitted.shown.length) return fitted;
  const shown = items.slice(at - fitted.shown.length + 1, at + 1);
  return { shown, more: items.length - at - 1 };
}

/** The progress ring's geometry (46px, 4px stroke). */
const RING = { size: 46, stroke: 4 } as const;
const RING_RADIUS = (RING.size - RING.stroke) / 2;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** How much of the list is done, a check once it all is. */
function ProgressRing({ done, total }: { done: number; total: number }) {
  const complete = done === total;
  return (
    <span
      className="relative grid shrink-0 place-items-center text-[color:var(--color-accent)]"
      style={{ width: RING.size, height: RING.size }}
      data-testid="todo-widget-ring"
    >
      <svg
        aria-hidden="true"
        className="absolute inset-0 -rotate-90"
        viewBox={`0 0 ${RING.size} ${RING.size}`}
      >
        <circle
          cx={RING.size / 2}
          cy={RING.size / 2}
          r={RING_RADIUS}
          fill="none"
          strokeWidth={RING.stroke}
          className="stroke-[var(--widget-line,var(--card-line))]"
        />
        <circle
          cx={RING.size / 2}
          cy={RING.size / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={RING.stroke}
          strokeLinecap="round"
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - done / total)}
        />
      </svg>
      {complete && (
        <Icons.Check
          className="h-[18px] w-[18px]"
          strokeWidth={3}
          aria-hidden="true"
        />
      )}
    </span>
  );
}

/**
 * A checklist on the desktop. Small: its title and progress ring. Medium
 * and large: its rows, each ticked and typed in place (the checklist's own
 * save), and a line to add one. The row being typed in always stays shown.
 */
export function TodoWidgetFace({ frame, size }: WidgetFaceProps) {
  const t = useT(todoFrameMessages);
  // The project this window shows (a pin window, the pin's).
  const projectId = useProjectStore((state) => state.currentProject?.id);
  const edits = useTodoEdits({
    frameId: frame.frameId,
    content: frame.content,
    projectId,
  });
  // The rows a tick addresses by id: the content it saves, never the card
  // (a bounded copy without ids). A pinned frame always holds its content.
  const items = todoItemsFrom(frame.content?.items);
  // The row that takes focus next, and the row being typed in.
  const [focusId, setFocusId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  if (items.length === 0 && size === "small") {
    return <WidgetEmpty frame={frame} size={size} message={t("noTasks")} />;
  }
  const title = frameDisplayTitle(frame);
  const total = items.length;
  const done = items.filter((item) => item.done === true).length;
  const ratio = `${done}/${total}`;
  const progress =
    total === 0
      ? t("noTasks")
      : done === total
        ? `${t("allDone")} · ${ratio}`
        : t("progress", { done, total });

  if (size === "small") {
    return (
      <WidgetFaceRoot frame={frame} size={size}>
        <p className={cn("line-clamp-2", WIDGET_DISPLAY.s)}>{title}</p>
        <div className="mt-auto">
          <ProgressRing done={done} total={total} />
        </div>
        <p
          className={cn("truncate", WIDGET_MUTED)}
          data-testid="todo-widget-progress"
        >
          {progress}
        </p>
      </WidgetFaceRoot>
    );
  }

  // One line of the size stays for "Add a task".
  const { shown, more } = rowsShowing(
    items,
    ROWS[size] - 1,
    focusId ?? editingId,
  );
  const rows = (
    <div
      className={cn("grid", size === "large" ? "gap-[14px]" : "mt-1 gap-[9px]")}
    >
      {shown.map((item) => (
        <TodoEditRow
          key={item.id}
          item={item}
          edits={edits}
          appearance="widget"
          testIdPrefix="todo-widget"
          focused={focusId === item.id}
          onFocusRow={setFocusId}
          previousId={items[items.indexOf(item) - 1]?.id ?? null}
          onEditingChange={(editing) =>
            setEditingId((current) =>
              editing ? item.id : current === item.id ? null : current,
            )
          }
        />
      ))}
      <WidgetMore count={more} />
      <button
        type="button"
        className={cn("flex min-w-0 items-start gap-2 text-left", WIDGET_MUTED)}
        onClick={(event) => {
          event.stopPropagation();
          setFocusId(edits.addAfter(null));
        }}
        data-testid="todo-widget-add"
      >
        <WidgetCheckMark done={false} />
        <span className="truncate">{t("addTask")}</span>
      </button>
    </div>
  );

  if (size === "medium") {
    return (
      <WidgetFaceRoot frame={frame} size={size}>
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <p className={cn("truncate", WIDGET_DISPLAY.s)}>{title}</p>
          <span className={cn("shrink-0 tabular-nums", WIDGET_MUTED)}>
            {ratio}
          </span>
        </div>
        {rows}
      </WidgetFaceRoot>
    );
  }

  return (
    <WidgetFaceRoot frame={frame} size={size}>
      <p className={cn("line-clamp-2", WIDGET_DISPLAY.s)}>{title}</p>
      <p className={WIDGET_MUTED} data-testid="todo-widget-progress">
        {progress}
      </p>
      <WidgetRule className="my-1.5" />
      {rows}
    </WidgetFaceRoot>
  );
}
